import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DaysOfWeekFlags } from '../../../models/days-of-week-flags.enum';
import { NotificationType } from '../../../models/notification-type.enum';
import {
  OfficeNotificationSettingDto,
  UpsertOfficeNotificationSettingRequest,
} from '../../../models/office-notification.models';
import { PracticeArea } from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { OfficeNotificationSettingsService } from '../../../services/office-notification-settings.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';

/** A firm-wide practice area with this office's per-practice-area recipient toggle. */
interface NotificationPracticeArea {
  code: string;
  name: string;
  enabled: boolean;
}

/**
 * Office-level notifications card on the office details page. Configures the "Resource capacity
 * reminder" for an office: enable/disable, frequency (every weekday / specific weekdays), time zone,
 * the office-level email lock ({@link requiredForAllResources}), and per-practice-area targeting.
 *
 * Settings auto-save on each change — there is no explicit Save button. Config changes upsert the
 * setting; practice-area toggles hit the per-practice-area endpoint (creating the setting first if the
 * office has never configured it). The card is read-only for users with view-only permission.
 */
@Component({
  selector: 'app-office-notifications',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './office-notifications.component.html',
  styleUrls: ['./office-notifications.component.scss'],
  animations: [
    // Smoothly expand/collapse the card body and the schedule + practice-area
    // config (mirrors the export-config expandCollapse).
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class OfficeNotificationsComponent implements OnInit {
  private readonly service = inject(OfficeNotificationSettingsService);
  private readonly practiceAreaService = inject(PracticeAreaService);
  private readonly snackbar = inject(SnackbarService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Config auto-saves are funnelled through this subject so a newer edit cancels the older in-flight
   * upsert (switchMap) — rapid field changes can't leave two config PUTs racing or an out-of-order
   * response stomping component state.
   */
  private readonly configSave$ = new Subject<void>();

  /** Office code this card configures (drives every API call). */
  @Input() officeCode = '';

  /** Office name shown in the summary and recipients copy. */
  @Input() officeDescription = '';

  /** True while the initial config/lookup load is in flight. */
  isLoading = false;

  /** Set when the initial load fails; drives the inline error + retry. */
  loadError: string | null = null;

  /** False when the API exposes no office-scheduled notification types (nothing to configure). */
  hasType = false;

  /** The persisted setting's id; null until this office first configures the notification. */
  private settingId: number | null = null;

  /** The office-scheduled notification type this card configures (from the types lookup). */
  private notificationType: NotificationType =
    NotificationType.ResourceCapacityReminder;

  /** Whether the resource capacity reminder is enabled for this office. */
  digestEnabled = false;

  /** Whether the reminder card body is expanded (the whole card collapses). */
  notificationsExpanded = true;

  /**
   * When true, resources can't turn this notification's email off from their
   * profile (the office overrides the per-resource opt-out).
   */
  requiredForAllResources = false;

  /** Office-level IANA time zone the fixed noon send time is interpreted in. */
  notificationTimeZone = 'America/New_York';

  /** Time-zone options for the picker, sourced from the API (IANA ids + offset labels). */
  notificationTimeZones: { value: string; label: string }[] = [];

  /** 'daily' selects every weekday; 'specific' lets the user pick weekdays. */
  notificationFrequency: 'daily' | 'specific' = 'specific';

  // Weekends are intentionally excluded — the notification never sends on
  // Saturday or Sunday (enforced by API validation and the DB check constraint).
  readonly weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  readonly weekDayLabels: Record<string, string> = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
  };

  /** Maps a weekday label to its {@link DaysOfWeekFlags} bit. */
  private readonly dayFlags: Record<string, DaysOfWeekFlags> = {
    Mon: DaysOfWeekFlags.Mon,
    Tue: DaysOfWeekFlags.Tue,
    Wed: DaysOfWeekFlags.Wed,
    Thu: DaysOfWeekFlags.Thu,
    Fri: DaysOfWeekFlags.Fri,
  };

  /** Selected send days (as weekday labels). */
  selectedDays: string[] = [];

  /**
   * Fixed send time (noon, local to the selected time zone). Not user-editable
   * for now — every reminder sends at 12:00 PM.
   */
  readonly sendTime = '12:00';

  /** Firm-wide practice areas merged with this office's per-practice-area toggles. */
  notificationPracticeAreas: NotificationPracticeArea[] = [];

  /** Free-text filter for the practice-area table. */
  practiceAreaFilter = '';

  /** Backs the paginated practice-area table (filtering and paging are client-side). */
  readonly practiceAreaDataSource =
    new MatTableDataSource<NotificationPracticeArea>([]);

  readonly practiceAreaColumns = ['practiceArea', 'state', 'enabled'];

  // Not round-tripped on purpose: the paginator owns its own page size, and a remount deliberately
  // returns to this default rather than restoring the user's last choice.
  readonly practiceAreaPageSize = 5;

  readonly practiceAreaPageSizeOptions = [5, 10, 25, 50];

  private readonly practiceAreaPaginator = viewChild(MatPaginator);

  constructor() {
    // The paginator lives inside two @if blocks (card expanded, reminder enabled), so it comes and
    // goes. Assign unconditionally rather than only on a truthy value: attaching a new instance and
    // releasing a destroyed one are the same operation. Without the release the data source stays
    // merged onto a dead paginator's `page` stream and keeps writing `length` into it.
    // `?? null` bridges viewChild's `MatPaginator | undefined` to the setter's `MatPaginator | null`.
    effect(() => {
      this.practiceAreaDataSource.paginator =
        this.practiceAreaPaginator() ?? null;
    });

    // Match on the code and name only. The default predicate stringifies every property, so the
    // `enabled` flag would leak into matches (typing "true" would filter to the enabled rows).
    this.practiceAreaDataSource.filterPredicate = (pa, filter) =>
      pa.name.toLowerCase().includes(filter) ||
      pa.code.toLowerCase().includes(filter);
  }

  ngOnInit(): void {
    // Config auto-save: a newer change cancels the older in-flight upsert (switchMap), so out-of-order
    // responses can't stomp state. The inner catchError keeps the long-lived stream alive after a
    // failed save (an error on the outer stream would kill it and silence all future saves).
    this.configSave$
      .pipe(
        switchMap(() =>
          this.upsertConfig$().pipe(
            map(() => null),
            catchError((err) => of(err)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((error) => {
        // Auto-save succeeds silently (the control reflecting the change is the feedback); surfacing a
        // toast per change would stack one for every rapid edit. Only failures are announced.
        if (error) {
          this.snackbar.showError(
            describeApiError(error, 'Failed to save notification settings'),
          );
          // Resync so a failed auto-save doesn't leave a control (enable, days, time zone, email lock)
          // showing an unsaved value the server never accepted.
          this.loadData();
        }
      });

    this.loadData();
  }

  loadData(): void {
    if (!this.officeCode) {
      return;
    }

    this.isLoading = true;
    this.loadError = null;

    forkJoin({
      settings: this.service.getByOffice(this.officeCode),
      types: this.service.getTypes(),
      timeZones: this.service.getTimeZones(),
      practiceAreas: this.practiceAreaService.getAllPracticeAreas(),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ settings, types, timeZones, practiceAreas }) => {
        // One office-scheduled type today (Resource capacity reminder); the API
        // is the source of truth for its metadata and default days.
        const typeDto = types[0] ?? null;
        this.hasType = typeDto !== null;
        if (!typeDto) {
          this.isLoading = false;
          return;
        }
        this.notificationType = typeDto.type;

        const setting = settings.find((s) => s.type === typeDto.type) ?? null;
        this.settingId = setting?.id ?? null;
        this.digestEnabled = setting?.enabled ?? false;
        this.requiredForAllResources =
          setting?.requireEmailForAllResources ?? false;
        this.notificationTimeZone =
          setting?.timeZoneId ?? this.defaultTimeZone(timeZones);

        // Build the picker once, now that the zone is resolved. The persisted zone (or the default)
        // might not be in the fetched list — e.g. the endpoint no longer returns it — so fold it in
        // first and let buildTimeZoneOptions sort the whole set, rather than building the list and
        // then rebuilding it. Without this the dropdown would render blank for a valid config, which
        // makes it look mis-set. The truthiness guard matters because setting?.timeZoneId is
        // unvalidated: an empty string would otherwise add a blank option.
        const zoneIds =
          this.notificationTimeZone &&
          !timeZones.includes(this.notificationTimeZone)
            ? [...timeZones, this.notificationTimeZone]
            : timeZones;
        this.notificationTimeZones = this.buildTimeZoneOptions(zoneIds);

        const days = setting?.days ?? typeDto.defaultDays;
        this.selectedDays = this.bitmaskToDays(days);
        this.notificationFrequency = this.isEveryWeekday(this.selectedDays)
          ? 'daily'
          : 'specific';

        this.notificationPracticeAreas = this.mergePracticeAreas(
          practiceAreas,
          setting,
        );
        this.practiceAreaDataSource.data = this.notificationPracticeAreas;

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading office notification settings:', err);
        this.loadError = 'Failed to load notification settings.';
        this.isLoading = false;
      },
    });
  }

  retry(): void {
    this.loadData();
  }

  /** True when the current user may change these settings (else the card is read-only). */
  get canManage(): boolean {
    return this.authService.canManageOfficeNotifications();
  }

  onPracticeAreaFilterChange(value: string): void {
    this.practiceAreaFilter = value;
    // MatTableDataSource hands the filter string to the predicate verbatim, so normalize here to
    // keep the predicate a straight case-insensitive comparison.
    this.practiceAreaDataSource.filter = value.trim().toLowerCase();
    // Narrowing the results while parked on a later page would otherwise strand the user on the tail
    // of the matches — MatTableDataSource only clamps pageIndex to the last valid page, it never
    // resets it — so jump back to the first page, as every other paginated table here does.
    this.practiceAreaPaginator()?.firstPage();
  }

  /** Clears the filter, which also returns the table to the first page. */
  clearPracticeAreaFilter(): void {
    this.onPracticeAreaFilterChange('');
  }

  get enabledPracticeAreaCount(): number {
    return this.notificationPracticeAreas.filter((pa) => pa.enabled).length;
  }

  /** In "daily" mode the day picker is fixed to all weekdays. */
  get daysLocked(): boolean {
    return this.notificationFrequency === 'daily';
  }

  setNotificationFrequency(mode: 'daily' | 'specific'): void {
    if (this.notificationFrequency === mode) {
      return;
    }
    this.notificationFrequency = mode;
    this.onNotificationFrequencyChange();
  }

  onNotificationFrequencyChange(): void {
    if (this.notificationFrequency === 'daily') {
      this.selectedDays = [...this.weekDays];
    }
    this.onNotificationChange();
  }

  toggleDay(day: string): void {
    if (this.daysLocked) {
      return;
    }
    const isSelected = this.selectedDays.includes(day);
    // Enforce the API's "at least one weekday" invariant client-side: deselecting the last day would
    // leave an unsavable empty selection, so block it (and point at the off switch, which is how you
    // actually stop the reminder). This keeps selectedDays non-empty everywhere else.
    if (isSelected && this.selectedDays.length === 1) {
      this.snackbar.showInfo('Keep at least one day selected, or turn the reminder off to stop it.');
      return;
    }
    this.selectedDays = isSelected
      ? this.selectedDays.filter((d) => d !== day)
      : [...this.selectedDays, day];
    this.onNotificationChange();
  }

  /**
   * Auto-save the office-level config (enable, days, time zone, email lock). No-ops only when there's
   * nothing to configure (no registered type) or the user lacks write permission. The save is funnelled
   * through {@link configSave$} so a newer change cancels an older in-flight upsert (wired in ngOnInit);
   * a day selection is always non-empty (see {@link toggleDay}), and success/error feedback (including
   * the resync on failure) is handled there.
   */
  onNotificationChange(): void {
    if (!this.canManage || !this.hasType) {
      return;
    }
    this.configSave$.next();
  }

  /** Auto-save a single practice-area toggle (creating the setting first if it doesn't exist yet). */
  onPracticeAreaChange(pa: NotificationPracticeArea): void {
    if (!this.canManage) {
      return;
    }
    // A practice-area toggle needs an existing setting id. If the office has never configured this
    // notification, ensureSettingId$ creates the config first (with the current, always-valid day
    // selection), then applies the toggle.
    this.ensureSettingId$()
      .pipe(
        switchMap((id) =>
          this.service.upsertPracticeArea(id, pa.code, pa.enabled),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (err) => {
          pa.enabled = !pa.enabled; // revert the optimistic toggle
          this.snackbar.showError(describeApiError(err, 'Failed to save practice area'));
        },
      });
  }

  /** Resolves to the setting id, creating the config on the fly if the office has none yet. */
  private ensureSettingId$(): Observable<number> {
    return this.settingId != null
      ? of(this.settingId)
      : this.upsertConfig$().pipe(map((dto) => dto.id));
  }

  private upsertConfig$(): Observable<OfficeNotificationSettingDto> {
    const request: UpsertOfficeNotificationSettingRequest = {
      officeCode: this.officeCode,
      type: this.notificationType,
      enabled: this.digestEnabled,
      timeZoneId: this.notificationTimeZone,
      days: this.daysToBitmask(this.selectedDays),
      requireEmailForAllResources: this.requiredForAllResources,
    };
    return this.service
      .upsert(request)
      .pipe(tap((dto) => (this.settingId = dto.id)));
  }

  private mergePracticeAreas(
    practiceAreas: PracticeArea[],
    setting: OfficeNotificationSettingDto | null,
  ): NotificationPracticeArea[] {
    const enabledByCode = new Map(
      (setting?.practiceAreas ?? []).map((pa) => [
        pa.practiceAreaCode.toLowerCase(),
        pa.enabled,
      ]),
    );
    // Deliberately no `isActive` filter. The API never sends the flag — PracticeAreaDto carries no
    // such property — so practice-area.service.ts:68 (`isActive: practiceArea.isActive ?? true`)
    // always falls back to true and filtering on it was a guaranteed no-op.
    return practiceAreas.map((pa) => ({
      code: pa.practiceCode,
      name: pa.description,
      enabled: enabledByCode.get(pa.practiceCode.toLowerCase()) ?? false,
    }));
  }

  private daysToBitmask(days: string[]): DaysOfWeekFlags {
    return days.reduce(
      (mask, d) => mask | (this.dayFlags[d] ?? DaysOfWeekFlags.None),
      DaysOfWeekFlags.None,
    );
  }

  private bitmaskToDays(mask: DaysOfWeekFlags): string[] {
    return this.weekDays.filter((d) => (mask & this.dayFlags[d]) !== 0);
  }

  private isEveryWeekday(days: string[]): boolean {
    return (
      days.length === this.weekDays.length &&
      this.weekDays.every((d) => days.includes(d))
    );
  }

  private defaultTimeZone(zones: string[]): string {
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserZone && zones.includes(browserZone)) {
      return browserZone;
    }
    if (zones.includes('America/New_York')) {
      return 'America/New_York';
    }
    return zones[0] ?? 'America/New_York';
  }

  /**
   * Builds the picker's options ordered west-to-east by UTC offset rather than alphabetically by
   * IANA id, so the list reads like a timeline and the "(GMT-06:00)" prefixes ascend down it. Zones
   * sharing an offset fall back to their id, keeping the order stable across loads.
   */
  private buildTimeZoneOptions(
    ids: string[],
  ): { value: string; label: string }[] {
    return ids
      .map((id) => {
        // Resolve the offset once per id. Both the "(GMT-05:00) America/New_York" label and the sort
        // key derive from this one formatted string, so the order can never disagree with the prefix
        // the user reads — and each zone costs one Intl.DateTimeFormat instead of two.
        const offset = this.timeZoneOffsetLabel(id);
        return {
          value: id,
          label: offset ? `(${offset}) ${id}` : id,
          offsetMinutes: this.offsetLabelToMinutes(offset),
        };
      })
      .sort(
        (a, b) =>
          a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value),
      )
      .map(({ value, label }) => ({ value, label }));
  }

  /**
   * A formatted UTC offset converted to minutes, negative west of UTC. UTC itself formats as a bare
   * "GMT" and correctly yields 0; an unrecognized id (null offset) also sorts as UTC rather than
   * dropping out of the list.
   */
  private offsetLabelToMinutes(offset: string | null): number {
    const match = offset?.match(/^GMT([+-])(\d{2}):(\d{2})$/);
    if (!match) {
      return 0;
    }
    const minutes = Number(match[2]) * 60 + Number(match[3]);
    return match[1] === '-' ? -minutes : minutes;
  }

  /** The zone's formatted UTC offset ("GMT-05:00", or "GMT" for UTC); null if the id is invalid. */
  private timeZoneOffsetLabel(id: string): string | null {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: id,
        timeZoneName: 'longOffset',
      }).formatToParts(new Date());
      return parts.find((p) => p.type === 'timeZoneName')?.value ?? null;
    } catch {
      return null;
    }
  }

  /** Human-readable "every weekday" / "on Mon, Wed" phrase for the summary. */
  get notificationScheduleDaysPhrase(): string {
    const selected = this.weekDays.filter((d) => this.selectedDays.includes(d));
    if (this.isEveryWeekday(selected)) {
      return 'every weekday';
    }
    if (selected.length === 0) {
      return 'on no days yet';
    }
    return 'on ' + this.joinWithAnd(selected);
  }

  get notificationScheduleTimesPhrase(): string {
    return this.formatTime(this.sendTime);
  }

  get notificationTimeZoneLabel(): string {
    const tz = this.notificationTimeZones.find(
      (t) => t.value === this.notificationTimeZone,
    );
    return tz ? tz.label : this.notificationTimeZone;
  }

  private formatTime(hhmm: string): string {
    const [hoursStr, minutes] = hhmm.split(':');
    let hours = parseInt(hoursStr, 10);
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) {
      hours = 12;
    }
    return `${hours}:${minutes} ${meridiem}`;
  }

  private joinWithAnd(items: string[]): string {
    if (items.length === 0) {
      return '';
    }
    if (items.length === 1) {
      return items[0];
    }
    if (items.length === 2) {
      return `${items[0]} and ${items[1]}`;
    }
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  }
}
